import React, { useState, useEffect, useContext } from "react";
import { ThemeContext } from "../../../../context/ThemeContext";
import { getData, postData, putData, deleteData } from "../../../../services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const HideIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const ShowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const TagIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const QuestionIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────

const StatusBadge = ({ status, theme }) => {
  const config = {
    Active: { bg: `${theme.success}1a`, color: theme.success, dot: theme.success },
    Hidden: { bg: `${theme.textMuted}1a`, color: theme.textMuted, dot: theme.textMuted },
  };
  const c = config[status] || config.Active;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: 600,
        background: c.bg,
        color: c.color,
      }}
    >
      <span
        style={{
          width: "5px",
          height: "5px",
          borderRadius: "50%",
          background: c.dot,
        }}
      />
      {status}
    </span>
  );
};

const StatCard = ({ title, count, icon, color, theme }) => (
  <div
    style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "14px",
      padding: "18px 20px",
      textAlign: "center",
      flex: 1,
      minWidth: "140px",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      cursor: "pointer",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = `0 8px 20px ${color || theme.primary}15`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}
  >
    {icon && (
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "10px",
          background: `${color || theme.primary}1a`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: color || theme.primary,
          margin: "0 auto 12px",
          fontSize: "18px",
        }}
      >
        {icon}
      </div>
    )}
    <div
      style={{
        fontSize: "28px",
        fontWeight: 700,
        color: theme.textPrimary,
      }}
    >
      {count}
    </div>
    <div
      style={{
        fontSize: "11px",
        fontWeight: 500,
        color: theme.textMuted,
        marginTop: "4px",
      }}
    >
      {title}
    </div>
  </div>
);

const CategoryTab = ({ name, isActive, onClick, theme, count }) => (
  <button
    onClick={onClick}
    style={{
      padding: "8px 18px",
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: 500,
      background: isActive ? theme.primary : "transparent",
      color: isActive ? "#fff" : theme.textSecondary,
      border: isActive ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
    }}
    onMouseEnter={(e) => {
      if (!isActive) {
        e.currentTarget.style.background = theme.surfaceLighter;
        e.currentTarget.style.color = theme.textPrimary;
      }
    }}
    onMouseLeave={(e) => {
      if (!isActive) {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = theme.textSecondary;
      }
    }}
  >
    {name}
    {count !== undefined && count > 0 && (
      <span
        style={{
          background: isActive ? "rgba(255,255,255,0.2)" : `${theme.primary}1a`,
          color: isActive ? "#fff" : theme.primary,
          padding: "1px 7px",
          borderRadius: "10px",
          fontSize: "10px",
          fontWeight: 700,
        }}
      >
        {count}
      </span>
    )}
  </button>
);

const FAQItem = ({ faq, theme, onToggleStatus, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${isOpen ? `${theme.primary}40` : theme.border}`,
        borderRadius: "12px",
        marginBottom: "12px",
        overflow: "hidden",
        transition: "all 0.2s ease",
        boxShadow: isOpen ? `0 4px 12px ${theme.primary}10` : "none",
      }}
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          cursor: "pointer",
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = theme.surfaceLighter;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <div style={{ flex: 1, paddingRight: "16px" }}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: theme.textPrimary,
              marginBottom: "8px",
            }}
          >
            {faq.question}
          </div>
          <div
            style={{
              display: "flex",
              gap: "16px",
              fontSize: "11px",
              color: theme.textMuted,
              flexWrap: "wrap",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <TagIcon /> {faq.category}
            </span>
            {faq.date && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <CalendarIcon /> {faq.date}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
          <StatusBadge status={faq.status} theme={theme} />
          <div
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              color: theme.textMuted,
            }}
          >
            <ChevronDownIcon />
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            padding: "16px",
            borderTop: `1px solid ${theme.border}`,
            background: theme.surfaceLighter,
            animation: "fadeIn 0.2s ease",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              color: theme.textSecondary,
              lineHeight: 1.6,
              marginBottom: "16px",
            }}
          >
            {faq.answer}
          </div>
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => onToggleStatus(faq.id)}
              style={{
                background: "none",
                border: `1px solid ${theme.border}`,
                cursor: "pointer",
                color: theme.textSecondary,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 500,
                padding: "6px 12px",
                borderRadius: "6px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${theme.warning}1a`;
                e.currentTarget.style.color = theme.warning;
                e.currentTarget.style.borderColor = theme.warning;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
                e.currentTarget.style.color = theme.textSecondary;
                e.currentTarget.style.borderColor = theme.border;
              }}
            >
              {faq.status === "Active" ? <HideIcon /> : <ShowIcon />}
              {faq.status === "Active" ? "Hide" : "Show"}
            </button>
            <button
              onClick={() => onEdit(faq)}
              style={{
                background: "none",
                border: `1px solid ${theme.border}`,
                cursor: "pointer",
                color: theme.textSecondary,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 500,
                padding: "6px 12px",
                borderRadius: "6px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${theme.primary}1a`;
                e.currentTarget.style.color = theme.primary;
                e.currentTarget.style.borderColor = theme.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
                e.currentTarget.style.color = theme.textSecondary;
                e.currentTarget.style.borderColor = theme.border;
              }}
            >
              <EditIcon /> Edit
            </button>
            <button
              onClick={() => onDelete(faq.id)}
              style={{
                background: "none",
                border: `1px solid ${theme.border}`,
                cursor: "pointer",
                color: theme.textSecondary,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                fontWeight: 500,
                padding: "6px 12px",
                borderRadius: "6px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${theme.error || theme.danger}1a`;
                e.currentTarget.style.color = theme.error || theme.danger;
                e.currentTarget.style.borderColor = theme.error || theme.danger;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
                e.currentTarget.style.color = theme.textSecondary;
                e.currentTarget.style.borderColor = theme.border;
              }}
            >
              <DeleteIcon /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const FAQ = () => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("manage");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    designation: "",
    ques: "",
    ans: "",
    status: "Active",
    pic: "",
    sessionid: "",
  });

  // ── API calls ──
  const fetchCategories = async () => {
    try {
      const data = await getData("faqRouter/categories");
      if (data.success) {
        setCategories(data.data.map((c) => c.name));
      }
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const fetchFAQs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== "All Categories")
        params.append("category", selectedCategory);
      if (search) params.append("search", search);
      const url = `faqRouter/?${params.toString()}`;
      const data = await getData(url);
      if (data.success) {
        const mapped = data.data.map((item) => ({
          id: item.id,
          category: item.designation,
          question: item.ques,
          answer: item.ans,
          status: item.status,
          date: item.created_at ? item.created_at.slice(0, 10) : "",
          ...item,
        }));
        setFaqs(mapped);
      } else {
        setError("Failed to load FAQs");
      }
    } catch (err) {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchFAQs();
  }, [selectedCategory, search]);

  // ── Stats ──
  const activeFaqs = faqs.filter((f) => f.status === "Active").length;
  const hiddenFaqs = faqs.filter((f) => f.status === "Hidden").length;
  const activeCategories = categories.length;

  // Count FAQs per category
  const getCategoryCount = (cat) => {
    if (cat === "All Categories") return faqs.length;
    return faqs.filter((f) => f.category === cat).length;
  };

  // ── CRUD ──
  const toggleStatus = async (id) => {
    try {
      const data = await postData(`faqRouter/${id}/toggle-status`, {});
      if (data.success) {
        setFaqs((prev) =>
          prev.map((f) => (f.id === id ? { ...f, status: data.status } : f))
        );
      } else alert("Failed to toggle status");
    } catch (err) {
      alert("Network error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) return;
    try {
      const data = await deleteData(`faqRouter/${id}`);
      if (data.success) {
        setFaqs((prev) => prev.filter((f) => f.id !== id));
      } else alert("Failed to delete");
    } catch (err) {
      alert("Network error");
    }
  };

  const handleAddFAQ = () => {
    setEditingFaq(null);
    setFormData({
      designation:
        selectedCategory !== "All Categories" ? selectedCategory : categories[0] || "",
      ques: "",
      ans: "",
      status: "Active",
      pic: "",
      sessionid: "",
    });
    setShowModal(true);
  };

  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setFormData({
      designation: faq.designation || faq.category,
      ques: faq.ques || faq.question,
      ans: faq.ans || faq.answer,
      status: faq.status,
      pic: faq.pic || "",
      sessionid: faq.sessionid || "",
    });
    setShowModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      designation: formData.designation,
      ques: formData.ques,
      ans: formData.ans,
      status: formData.status,
      pic: formData.pic || null,
      sessionid: formData.sessionid || null,
    };

    try {
      let data;
      if (editingFaq) {
        data = await putData(`faqRouter/${editingFaq.id}`, payload);
      } else {
        data = await postData(`faqRouter`, payload);
      }
      if (data.success) {
        setShowModal(false);
        fetchFAQs();
      } else alert(data.message || "Operation failed");
    } catch (err) {
      alert("Network error");
    }
  };

  // ── Client-side filter ──
  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory =
      selectedCategory === "All Categories" || faq.category === selectedCategory;
    const matchesSearch =
      faq.question.toLowerCase().includes(search.toLowerCase()) ||
      faq.answer.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // ── Styles ──
  const styles = {
    container: {
      background: "transparent",
      fontFamily: "'Segoe UI', 'DM Sans', sans-serif",
      padding: "20px 24px",
    },
    appTabs: {
      display: "flex",
      gap: "8px",
      marginBottom: "24px",
      flexWrap: "wrap",
      padding: "6px",
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "12px",
      width: "fit-content",
    },
    searchSection: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
      flexWrap: "wrap",
      gap: "16px",
    },
    searchWrapper: {
      position: "relative",
      flex: 1,
      maxWidth: "400px",
      minWidth: "250px",
    },
    searchIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: theme.primary,
      display: "flex",
      alignItems: "center",
    },
    searchInput: {
      width: "100%",
      padding: "10px 12px 10px 38px",
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
      fontSize: "13px",
      color: theme.textPrimary,
      outline: "none",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
    },
    viewToggle: {
      display: "flex",
      gap: "4px",
      padding: "4px",
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
    },
    viewBtn: (isActive) => ({
      padding: "6px 16px",
      borderRadius: "7px",
      fontSize: "12px",
      fontWeight: 500,
      background: isActive ? theme.primary : "transparent",
      color: isActive ? "#fff" : theme.textSecondary,
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
    }),
    statsRow: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: "16px",
      marginBottom: "24px",
    },
    categoryTabs: {
      display: "flex",
      gap: "10px",
      marginBottom: "24px",
      flexWrap: "wrap",
      alignItems: "center",
    },
    addBtn: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "10px 20px",
      background: theme.primary,
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: `0 4px 12px ${theme.primary}30`,
      marginLeft: "auto",
    },
    faqList: {
      marginTop: "20px",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px",
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "16px",
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.6)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 999,
      animation: "fadeIn 0.2s ease",
    },
    modal: {
      background: theme.surface,
      padding: "28px",
      borderRadius: "20px",
      width: "520px",
      maxWidth: "90%",
      maxHeight: "90vh",
      overflowY: "auto",
      border: `1px solid ${theme.border}`,
      boxShadow: `0 20px 60px ${theme.primary}30`,
      animation: "slideUp 0.3s ease",
    },
    modalTitle: {
      fontSize: "20px",
      fontWeight: 700,
      marginBottom: "20px",
      color: theme.textPrimary,
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    formGroup: {
      marginBottom: "16px",
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: 600,
      marginBottom: "6px",
      color: theme.textSecondary,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    input: {
      width: "100%",
      padding: "11px 14px",
      borderRadius: "10px",
      border: `1px solid ${theme.border}`,
      background: theme.surfaceLighter,
      color: theme.textPrimary,
      fontSize: "14px",
      outline: "none",
      transition: "border-color 0.2s ease",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "11px 14px",
      borderRadius: "10px",
      border: `1px solid ${theme.border}`,
      background: theme.surfaceLighter,
      color: theme.textPrimary,
      fontSize: "14px",
      outline: "none",
      cursor: "pointer",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "11px 14px",
      borderRadius: "10px",
      border: `1px solid ${theme.border}`,
      background: theme.surfaceLighter,
      color: theme.textPrimary,
      fontSize: "14px",
      minHeight: "100px",
      resize: "vertical",
      outline: "none",
      fontFamily: "inherit",
      boxSizing: "border-box",
    },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      marginTop: "24px",
      paddingTop: "20px",
      borderTop: `1px solid ${theme.border}`,
    },
    btnPrimary: {
      padding: "10px 24px",
      background: theme.primary,
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      boxShadow: `0 4px 12px ${theme.primary}30`,
      transition: "all 0.2s ease",
    },
    btnSecondary: {
      padding: "10px 24px",
      background: "transparent",
      color: theme.textSecondary,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 500,
      transition: "all 0.2s ease",
    },
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      {/* App Tabs (dynamic from categories) */}
      <div style={styles.appTabs}>
        {categories.map((cat) => (
          <button
            key={cat}
            style={{
              padding: "7px 16px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 600,
              background:
                selectedCategory === cat ? theme.primary : "transparent",
              color: selectedCategory === cat ? "#fff" : theme.textSecondary,
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Search & View Toggle */}
      <div style={styles.searchSection}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>
            <SearchIcon />
          </span>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Search FAQs by question or answer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}20`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = theme.border;
              e.currentTarget.style.boxShadow = "none";
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: theme.textMuted,
                padding: "4px",
                display: "flex",
                alignItems: "center",
                fontSize: "16px",
              }}
            >
              ✕
            </button>
          )}
        </div>
        <div style={styles.viewToggle}>
          <button
            style={styles.viewBtn(viewMode === "manage")}
            onClick={() => setViewMode("manage")}
          >
            ⚙️ Manage
          </button>
          <button
            style={styles.viewBtn(viewMode === "view")}
            onClick={() => setViewMode("view")}
          >
            👁️ View
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        <StatCard
          title="Active Categories"
          count={activeCategories}
          icon="📁"
          color={theme.primary}
          theme={theme}
        />
        <StatCard
          title="Active FAQs"
          count={activeFaqs}
          icon="✅"
          color={theme.success}
          theme={theme}
        />
        <StatCard
          title="Hidden FAQs"
          count={hiddenFaqs}
          icon="👁️‍🗨️"
          color={theme.warning}
          theme={theme}
        />
        <StatCard
          title="Total FAQs"
          count={faqs.length}
          icon="📋"
          color={theme.info || theme.primary}
          theme={theme}
        />
      </div>

      {/* Category Tabs */}
      <div style={styles.categoryTabs}>
        <CategoryTab
          name="All Categories"
          isActive={selectedCategory === "All Categories"}
          onClick={() => setSelectedCategory("All Categories")}
          theme={theme}
          count={getCategoryCount("All Categories")}
        />
        {categories.map((cat) => (
          <CategoryTab
            key={cat}
            name={cat}
            isActive={selectedCategory === cat}
            onClick={() => setSelectedCategory(cat)}
            theme={theme}
            count={getCategoryCount(cat)}
          />
        ))}
        <button
          style={styles.addBtn}
          onClick={handleAddFAQ}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}50`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}30`;
          }}
        >
          <PlusIcon /> Add FAQ
        </button>
      </div>

      {/* FAQ List */}
      <div style={styles.faqList}>
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px",
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: "16px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: `3px solid ${theme.border}`,
                borderTopColor: theme.primary,
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ color: theme.textMuted, fontSize: "14px" }}>
              Loading FAQs...
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px",
              background: theme.surface,
              border: `1px solid ${theme.error || theme.danger}30`,
              borderRadius: "16px",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: `${theme.error || theme.danger}1a`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                color: theme.error || theme.danger,
                fontSize: "24px",
              }}
            >
              ⚠️
            </div>
            <h3
              style={{
                color: theme.textPrimary,
                marginBottom: "8px",
                fontSize: "16px",
              }}
            >
              Error Loading FAQs
            </h3>
            <p style={{ color: theme.error || theme.danger, fontSize: "14px" }}>
              {error}
            </p>
          </div>
        ) : filteredFaqs.length === 0 ? (
          <div style={styles.emptyState}>
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                background: `${theme.primary}1a`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                color: theme.primary,
              }}
            >
              <QuestionIcon />
            </div>
            <h3
              style={{
                color: theme.textPrimary,
                marginBottom: "8px",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {search
                ? "No Matching FAQs"
                : selectedCategory !== "All Categories"
                ? `No FAQs in ${selectedCategory}`
                : "No FAQs Found"}
            </h3>
            <p style={{ color: theme.textMuted, fontSize: "13px", margin: 0 }}>
              {search
                ? "Try a different search term"
                : "Create your first FAQ to get started"}
            </p>
          </div>
        ) : (
          filteredFaqs.map((faq) => (
            <FAQItem
              key={faq.id}
              faq={faq}
              theme={theme}
              onToggleStatus={toggleStatus}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              {editingFaq ? (
                <>
                  <EditIcon /> Edit FAQ
                </>
              ) : (
                <>
                  <PlusIcon /> Add New FAQ
                </>
              )}
            </h3>
            <form onSubmit={handleFormSubmit}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Category *</label>
                <select
                  style={styles.select}
                  value={formData.designation}
                  onChange={(e) =>
                    setFormData({ ...formData, designation: e.target.value })
                  }
                  required
                  onFocus={(e) => (e.currentTarget.style.borderColor = theme.primary)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = theme.border)}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Question *</label>
                <input
                  style={styles.input}
                  type="text"
                  value={formData.ques}
                  onChange={(e) =>
                    setFormData({ ...formData, ques: e.target.value })
                  }
                  placeholder="Enter the question..."
                  required
                  onFocus={(e) => (e.currentTarget.style.borderColor = theme.primary)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = theme.border)}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Answer *</label>
                <textarea
                  style={styles.textarea}
                  value={formData.ans}
                  onChange={(e) =>
                    setFormData({ ...formData, ans: e.target.value })
                  }
                  placeholder="Write the answer here..."
                  required
                  onFocus={(e) => (e.currentTarget.style.borderColor = theme.primary)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = theme.border)}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select
                  style={styles.select}
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  onFocus={(e) => (e.currentTarget.style.borderColor = theme.primary)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = theme.border)}
                >
                  <option value="Active">✅ Active</option>
                  <option value="Hidden">👁️‍🗨️ Hidden</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Picture URL (optional)</label>
                <input
                  style={styles.input}
                  type="text"
                  value={formData.pic}
                  onChange={(e) =>
                    setFormData({ ...formData, pic: e.target.value })
                  }
                  placeholder="e.g., img_xxx.webp"
                  onFocus={(e) => (e.currentTarget.style.borderColor = theme.primary)}
                  onBlur={(e) => (e.currentTarget.style.borderColor = theme.border)}
                />
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  style={styles.btnSecondary}
                  onClick={() => setShowModal(false)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme.surfaceLighter;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.btnPrimary}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}50`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}30`;
                  }}
                >
                  {editingFaq ? "Update FAQ" : "Add FAQ"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQ;